/**
 * Cell streaming policy (P1-T05, SPEC-02 §1 + §3). Pure and deterministic —
 * no Babylon imports here so it runs in Vitest; the loader in streamingClient.ts
 * executes this plan against Babylon SceneLoader with worker parsing.
 */
import type { Tier } from '@kota-nova/shared';

export const CELL_SIZE = 128;

export interface Vec2i {
  i: number;
  j: number;
}

export function focusCell(x: number, z: number): Vec2i {
  return { i: Math.floor(x / CELL_SIZE), j: Math.floor(z / CELL_SIZE) };
}

/** ring = Chebyshev distance from focus cell */
export function ring(a: Vec2i, b: Vec2i): number {
  return Math.max(Math.abs(a.i - b.i), Math.abs(a.j - b.j));
}

/** nearest-edge distance from camera to a cell, metres (0 if inside) */
export function edgeDistance(camX: number, camZ: number, c: Vec2i): number {
  const x0 = c.i * CELL_SIZE;
  const x1 = x0 + CELL_SIZE;
  const z0 = c.j * CELL_SIZE;
  const z1 = z0 + CELL_SIZE;
  const dx = Math.max(x0 - camX, 0, camX - x1);
  const dz = Math.max(z0 - camZ, 0, camZ - z1);
  return Math.hypot(dx, dz);
}

export interface RingPolicyRow {
  lod0MaxRing: number;
  lod0EdgeRange: number | null; // Low: nearest-edge 48 m instead of ring
  lod0Max: number; // cap on concurrent LOD0 cells
  lod1MaxRing: number;
  skylineFromRing: number;
}

/** SPEC-02 §1 table. */
export const RING_POLICY: Record<Tier, RingPolicyRow> = {
  low: { lod0MaxRing: 0, lod0EdgeRange: 48, lod0Max: 4, lod1MaxRing: 2, skylineFromRing: 3 },
  mid: { lod0MaxRing: 1, lod0EdgeRange: null, lod0Max: 9, lod1MaxRing: 3, skylineFromRing: 4 },
  high: { lod0MaxRing: 1, lod0EdgeRange: null, lod0Max: 9, lod1MaxRing: 4, skylineFromRing: 5 },
};

export type Lod = 0 | 1;

export interface DesiredCell {
  cell: string; // "<i>_<j>"
  lod: Lod;
  ring: number;
}

/**
 * Desired LOD per cell for a camera pose (§3 step 2; altitude > 120 m drops
 * LOD0 to ring 0 on every tier). Returns only cells present in `available`
 * (cells known to the manifest).
 */
export function desiredSet(
  tier: Tier,
  camX: number,
  camZ: number,
  altitude: number,
  available: Set<string>,
  sliceBounds?: { min: number; max: number }, // only the built slice exists yet
): DesiredCell[] {
  const p = RING_POLICY[tier];
  const f = focusCell(camX, camZ);
  const lod0Ring = altitude > 120 ? 0 : p.lod0MaxRing;
  const out: DesiredCell[] = [];
  const span = Math.max(p.lod1MaxRing, 2);
  for (let di = -span; di <= span; di++) {
    for (let dj = -span; dj <= span; dj++) {
      const c = { i: f.i + di, j: f.j + dj };
      if (
        sliceBounds &&
        (c.i < sliceBounds.min || c.i > sliceBounds.max || c.j < sliceBounds.min || c.j > sliceBounds.max)
      )
        continue;
      const id = `${String(c.i)}_${String(c.j)}`;
      if (!available.has(id)) continue;
      const r = ring(f, c);
      const lod0 =
        r <= lod0Ring ||
        (p.lod0EdgeRange !== null && r <= 1 && edgeDistance(camX, camZ, c) <= p.lod0EdgeRange);
      if (lod0) {
        out.push({ cell: id, lod: 0, ring: r });
      } else if (r <= p.lod1MaxRing) {
        out.push({ cell: id, lod: 1, ring: r });
      }
    }
  }
  // cap LOD0 cells (Low: max 4) by nearest-edge distance
  const lod0s = out.filter((d) => d.lod === 0);
  if (lod0s.length > p.lod0Max) {
    lod0s
      .sort((a, b) => {
        const [ai, aj] = a.cell.split('_').map(Number);
        const [bi, bj] = b.cell.split('_').map(Number);
        return (
          edgeDistance(camX, camZ, { i: ai ?? 0, j: aj ?? 0 }) -
          edgeDistance(camX, camZ, { i: bi ?? 0, j: bj ?? 0 })
        );
      })
      .slice(p.lod0Max)
      .forEach((d) => (d.lod = 1));
  }
  return out.sort((a, b) => a.ring - b.ring || a.cell.localeCompare(b.cell));
}

/** Load priority (§3 step 3): ring first, then angle to camera forward. */
export function loadPriority(
  want: DesiredCell[],
  loadedLods: Map<string, Lod>,
  camX: number,
  camZ: number,
  forwardX: number,
  forwardZ: number,
): DesiredCell[] {
  const flen = Math.hypot(forwardX, forwardZ) || 1;
  const fx = forwardX / flen;
  const fz = forwardZ / flen;
  const needed = want.filter((d) => loadedLods.get(d.cell) !== d.lod);
  const score = (d: DesiredCell): number => {
    const [i, j] = d.cell.split('_').map((s) => Number(s));
    const cx = ((i ?? 0) + 0.5) * CELL_SIZE - camX;
    const cz = ((j ?? 0) + 0.5) * CELL_SIZE - camZ;
    const len = Math.hypot(cx, cz) || 1;
    const dot = (cx * fx + cz * fz) / len; // 1 = straight ahead
    return d.ring * 10 - dot;
  };
  return needed.sort((a, b) => score(a) - score(b));
}

/** LRU cell cache keyed "<cell>:lod", budget in MB, evict beyond ring `keepRing`. */
export class CellLru<V> {
  budgetMB = 64; // __KN.streaming.lruMB test hook mutates this directly
  private order: string[] = []; // most recent last
  private items = new Map<string, { value: V; mb: number }>();

  get sizeMB(): number {
    let t = 0;
    for (const it of this.items.values()) t += it.mb;
    return t;
  }

  get size(): number {
    return this.items.size;
  }

  has(key: string): boolean {
    return this.items.has(key);
  }

  get(key: string): V | undefined {
    const it = this.items.get(key);
    if (!it) return undefined;
    this.order = this.order.filter((k) => k !== key);
    this.order.push(key);
    return it.value;
  }

  /** Insert and evict oldest until within budget. Returns evicted keys. */
  put(key: string, value: V, mb: number): string[] {
    this.items.set(key, { value, mb });
    this.order = this.order.filter((k) => k !== key);
    this.order.push(key);
    return this.evict();
  }

  /** Remove and return evicted keys to fit the (possibly just-lowered) budget. */
  evict(): string[] {
    const evicted: string[] = [];
    while (this.sizeMB > this.budgetMB && this.order.length > 0) {
      const k = this.order.shift();
      if (k === undefined) break;
      this.items.delete(k);
      evicted.push(k);
    }
    return evicted;
  }

  drop(key: string): void {
    this.items.delete(key);
    this.order = this.order.filter((k) => k !== key);
  }

  keys(): string[] {
    return [...this.order];
  }
}

/** §3 step 5: unload cells beyond ring 5 after 10 s. */
export function shouldUnload(focus: Vec2i, cell: string, ageS: number): boolean {
  const [i, j] = cell.split('_').map((s) => Number(s));
  const r = ring(focus, { i: i ?? 0, j: j ?? 0 });
  return r > 5 && ageS > 10;
}

export const PLAN = { tickS: 0.35, parallel: 2, fadeS: 0.3, workSliceMs: 4 } as const;
